import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/api/client";
import Layout from "@/components/Layout";
import Modal from "@/components/Modal";
import { toast } from "sonner";

const WEEKDAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const hm = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const errMsg = (e) => formatApiErrorDetail(e?.response?.data?.detail);

function gridDays(month) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = addDays(first, -((first.getDay() + 6) % 7));
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

function eventDays(ev) {
  if (ev.all_day) {
    const days = [];
    for (let d = new Date(ev.start + "T00:00:00"); ymd(d) < ev.end; d = addDays(d, 1)) days.push(ymd(d));
    return days.length ? days : [ev.start];
  }
  const s = new Date(ev.start), e = new Date(ev.end), days = [];
  for (let d = new Date(s.getFullYear(), s.getMonth(), s.getDate()); d < e || ymd(d) === ymd(s); d = addDays(d, 1)) days.push(ymd(d));
  return days;
}

const emptyForm = (day) => ({ title: "", all_day: false, date: day, end_date: day, start_time: "09:00", end_time: "10:00", location: "", description: "" });

function formFromEvent(ev) {
  if (ev.all_day) {
    const end = ymd(addDays(new Date(ev.end + "T00:00:00"), -1));
    return { ...emptyForm(ev.start), title: ev.title, all_day: true, end_date: end < ev.start ? ev.start : end, location: ev.location, description: ev.description };
  }
  const s = new Date(ev.start), e = new Date(ev.end);
  return { title: ev.title, all_day: false, date: ymd(s), end_date: ymd(e), start_time: hm(s), end_time: hm(e), location: ev.location, description: ev.description };
}

function SetupPanel({ status, onConnect }) {
  if (!status.configured) {
    return (
      <div className="panel" data-testid="calendar-not-configured">
        <div className="panel-body gcal-setup">
          <div className="gcal-setup-icon"><i className="fa-brands fa-google" /></div>
          <h3>Credenziali Google da configurare</h3>
          <p>Per collegare il calendario serve un Client ID OAuth di Google Cloud (API Google Calendar abilitata). Inserisci l'URI di reindirizzamento qui sotto nelle credenziali e poi comunica Client ID e Client Secret.</p>
          <code data-testid="calendar-redirect-uri">{status.redirect_uri}</code>
        </div>
      </div>
    );
  }
  return (
    <div className="panel" data-testid="calendar-not-connected">
      <div className="panel-body gcal-setup">
        <div className="gcal-setup-icon"><i className="fa-brands fa-google" /></div>
        <h3>Collega il tuo Google Calendar</h3>
        <p>Accedi con il tuo account Google per vedere, creare e modificare gli eventi direttamente da GymERP.</p>
        <button className="btn primary" onClick={onConnect} data-testid="calendar-connect-btn">
          <i className="fa-brands fa-google" /> Collega Google Calendar
        </button>
      </div>
    </div>
  );
}

function MonthGrid({ month, events, onDay, onEvent }) {
  const today = ymd(new Date());
  const byDay = useMemo(() => {
    const m = {};
    events.forEach((ev) => eventDays(ev).forEach((d) => { (m[d] = m[d] || []).push(ev); }));
    return m;
  }, [events]);
  return (
    <div className="cal-grid" data-testid="calendar-grid">
      {WEEKDAYS.map((w) => <div key={w} className="cal-weekday">{w}</div>)}
      {gridDays(month).map((d) => {
        const key = ymd(d);
        const cls = ["cal-day", d.getMonth() !== month.getMonth() && "out", key === today && "today"].filter(Boolean).join(" ");
        return (
          <div key={key} className={cls} onClick={() => onDay(key)} data-testid={`calendar-day-${key}`}>
            <span className="cal-num">{d.getDate()}</span>
            {(byDay[key] || []).slice(0, 3).map((ev) => (
              <button key={ev.id} className={`cal-event${ev.all_day ? " allday" : ""}`} data-testid={`calendar-event-${ev.id}`}
                onClick={(e) => { e.stopPropagation(); onEvent(ev); }} title={ev.title}>
                {!ev.all_day && <b>{hm(new Date(ev.start))}</b>} {ev.title}
              </button>
            ))}
            {(byDay[key] || []).length > 3 && <small className="cal-more">+{byDay[key].length - 3} altri</small>}
          </div>
        );
      })}
    </div>
  );
}

function EventForm({ form, setForm }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  return (
    <div className="form-grid">
      <div className="field full"><label>Titolo *</label><input value={form.title} onChange={set("title")} data-testid="event-title-input" placeholder="es. Consegna kit ASD Aurora" /></div>
      <label className="field full gcal-check"><input type="checkbox" checked={form.all_day} onChange={set("all_day")} data-testid="event-allday-checkbox" /> Tutto il giorno</label>
      <div className="field"><label>Data inizio</label><input type="date" value={form.date} onChange={set("date")} data-testid="event-start-date" /></div>
      <div className="field"><label>Data fine</label><input type="date" value={form.end_date} onChange={set("end_date")} data-testid="event-end-date" /></div>
      {!form.all_day && <>
        <div className="field"><label>Ora inizio</label><input type="time" value={form.start_time} onChange={set("start_time")} data-testid="event-start-time" /></div>
        <div className="field"><label>Ora fine</label><input type="time" value={form.end_time} onChange={set("end_time")} data-testid="event-end-time" /></div>
      </>}
      <div className="field full"><label>Luogo</label><input value={form.location} onChange={set("location")} data-testid="event-location-input" /></div>
      <div className="field full"><label>Descrizione</label><textarea rows="3" value={form.description} onChange={set("description")} data-testid="event-description-input" /></div>
    </div>
  );
}

export default function Calendario() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm(ymd(new Date())));

  const loadStatus = useCallback(async () => { const r = await api.get("/calendar/status"); setStatus(r.data); }, []);

  const loadEvents = useCallback(async () => {
    const days = gridDays(month);
    setLoading(true);
    try {
      const r = await api.get("/calendar/events", { params: { time_min: days[0].toISOString(), time_max: addDays(days[41], 1).toISOString() } });
      setEvents(r.data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [month]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { if (status?.connected) { loadEvents(); } }, [status?.connected, loadEvents]);
  useEffect(() => {
    const g = params.get("gcal");
    if (!g) { return; }
    if (g === "connected") toast.success("Google Calendar collegato");
    else toast.error(g === "denied" ? "Autorizzazione Google annullata" : "Collegamento Google non riuscito");
    setParams({}, { replace: true });
  }, [params, setParams]);

  const connect = async () => {
    try { const r = await api.get("/oauth/calendar/login"); window.location.href = r.data.authorization_url; }
    catch (e) { toast.error(errMsg(e)); }
  };
  const disconnect = async () => {
    if (!window.confirm("Scollegare Google Calendar?")) return;
    await api.post("/calendar/disconnect"); setEvents([]); toast.success("Calendario scollegato"); loadStatus();
  };

  const openNew = (day) => { setEditing(null); setForm(emptyForm(day)); setModal(true); };
  const openEdit = (ev) => { setEditing(ev); setForm(formFromEvent(ev)); setModal(true); };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Titolo richiesto");
    const body = form.all_day
      ? { title: form.title, all_day: true, start: form.date, end: form.end_date < form.date ? form.date : form.end_date, location: form.location, description: form.description }
      : { title: form.title, all_day: false, start: `${form.date}T${form.start_time}:00`, end: `${form.end_date}T${form.end_time}:00`, location: form.location, description: form.description };
    if (!body.all_day && body.end <= body.start) return toast.error("La fine deve essere successiva all'inizio");
    try {
      if (editing) await api.put(`/calendar/events/${editing.id}`, body);
      else await api.post("/calendar/events", body);
      toast.success("Evento salvato"); setModal(false); loadEvents();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    if (!window.confirm("Eliminare l'evento da Google Calendar?")) return;
    try { await api.delete(`/calendar/events/${editing.id}`); toast.success("Evento eliminato"); setModal(false); loadEvents(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const monthLabel = month.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  const shift = (n) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));

  return (
    <Layout topbarActions={status?.connected && (
      <button className="btn accent" onClick={() => openNew(ymd(new Date()))} data-testid="new-event-btn"><i className="fa-solid fa-plus" /> Nuovo evento</button>
    )}>
      <div className="section-title">
        <div><h2>Calendario</h2><p>Appuntamenti, consegne e scadenze sincronizzati con Google Calendar.</p></div>
        {status?.connected && (
          <div className="gcal-account" data-testid="calendar-connected-account">
            <span className="pill ok"><i className="fa-brands fa-google" />&nbsp;{status.google_email || "Account Google"}</span>
            <button className="btn small" onClick={disconnect} data-testid="calendar-disconnect-btn"><i className="fa-solid fa-link-slash" /> Scollega</button>
          </div>
        )}
      </div>
      {!status ? <div className="panel"><div className="empty">Caricamento...</div></div>
        : !status.connected ? <SetupPanel status={status} onConnect={connect} />
        : (
          <div className="panel">
            <div className="panel-head">
              <h3 className="cal-title" data-testid="calendar-month-label">{monthLabel}{loading && <i className="fa-solid fa-spinner fa-spin" />}</h3>
              <div className="toolbar">
                <button className="btn small" onClick={() => shift(-1)} data-testid="calendar-prev-btn"><i className="fa-solid fa-chevron-left" /></button>
                <button className="btn small" onClick={() => { const d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)); }} data-testid="calendar-today-btn">Oggi</button>
                <button className="btn small" onClick={() => shift(1)} data-testid="calendar-next-btn"><i className="fa-solid fa-chevron-right" /></button>
              </div>
            </div>
            <MonthGrid month={month} events={events} onDay={openNew} onEvent={openEdit} />
          </div>
        )}

      <Modal open={modal} title={editing ? "Modifica evento" : "Nuovo evento"} onClose={() => setModal(false)} onSave={save}>
        <EventForm form={form} setForm={setForm} />
        {editing && (
          <div className="gcal-modal-actions">
            {editing.html_link && <a className="btn small" href={editing.html_link} target="_blank" rel="noreferrer" data-testid="event-open-google-link"><i className="fa-solid fa-up-right-from-square" /> Apri in Google</a>}
            <button className="btn small danger" onClick={del} data-testid="event-delete-btn"><i className="fa-solid fa-trash" /> Elimina evento</button>
          </div>
        )}
      </Modal>
    </Layout>
  );
}
